/**
 * File Utility functions.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright (c) Ircam amplify 2024
 */

export const FileUtil = {
  /**
   * Comvert a size in number of bytes to a size in number of kilobytes.
   *
   * @param sizeInBytes The size.
   * @returns The converted size.
   */
  sizeInKb: (sizeInBytes: number): number => {
    return Math.round(sizeInBytes * 0.001024)
  },

  /**
   * Convert a size in bytes to a string containing the size in kilobytes.
   *
   * @param sizeInBytes The size.
   * @return The string containing the converted size.
   */
  strSizeInKb: (sizeInBytes: number): string => {
    return `${FileUtil.sizeInKb(sizeInBytes).toLocaleString('US')}`
  },

  /**
   * Returns the extension of a file.
   *
   * @param fileName - The file name.
   * @returns The extension or undefined if not found.
   */
  getFileExtension: (fileName: string): string | undefined => {
    return fileName.split('.').pop()?.toLowerCase()
  },

  /**
   * Returns the mime-type matching the given file extension.
   *
   * @param ext The file extension, without the '.' character.
   * @returns The mime-type if found, else undefined.
   */
  getMimeTypeFromExt: (ext: string): string | undefined => {
    const mimeTypes: Record<string, string> = {
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      flac: 'audio/flac',
      gif: 'image/gif',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      json: 'application/json',
      mp3: 'audio/mpeg',
      mp4: 'video/mp4',
      pdf: 'application/pdf',
      png: 'image/png',
      rar: 'application/x-rar-compressed',
      txt: 'text/plain',
      wav: 'audio/wav',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      zip: 'application/zip',
      // Add more mappings as needed
    }

    return ext ? mimeTypes[ext] : undefined
  },

  /**
   * Returns true if the given mime type corresponds to an audio file.
   *
   * @param mimeType The mime type in string.
   */
  isAudioMimeType: (mimeType: string): boolean => {
    return mimeType.startsWith('audio/')
  },

  /**
   * Returns true if both given File objects represents the same local file.
   *
   * @param file1 The first file.
   * @param file2 The second file.
   * @returns True if files are the same.
   *    If both arguement are undefined, return false.
   */
  areFilesEqual: (file1?: File, file2?: File): boolean => {
    if (!file1 || !file2) {
      // If either file is undefined, return false
      return false
    }
    return (
      file1.name === file2.name &&
      file1.size === file2.size &&
      file1.lastModified === file2.lastModified
    )
  },
}
